// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PddjQueue } from '@interfaces/document.interfaces';
import { AppEvent, NavItem } from '@interfaces/interfaces';
import { User, UserDealers } from '@interfaces/user.interfaces';
import { DealershipService } from '@services/dealership.service';
import { DocumentsService } from '@services/documents.service';
import { NavService } from '@services/nav.service';
import { PreloadService } from '@services/preload.service';
import { UserService } from '@services/user.service';
import { Observable, takeWhile } from 'rxjs';

// file pddj-beta.png line 23, deprecated. Remove on next file update. 3/25/24
@Component({
    selector: 'pddj-header',
    template: `
        <section class="tenant">
            <img src="./../../../assets/img/1-ez-docs-white.png" (click)="routeTo()" />
            <button mat-button [routerLink]="['/pddj', 'help']">Need Help?</button>
        </section>
        <section class="dealer">
            <div class="left">
                <a (click)="routeTo()">
                    <img id="logo" alt="{{ currentDealershipName }} logo" />
                </a>
                <!-- <app-dealer *ngIf="userDealers?.length > 1" [userDealers]="userDealers" [dealerSelection]="selectedDealer" [user]="user"></app-dealer> -->
                <app-dealer *ngIf="selectedDealer" [dealerSelection]="selectedDealer" [user]="user" (outEvent)="eventHandler($event)"></app-dealer>
                <!-- <a [routerLink]="['/pddj', 'deals']" *ngIf="userDealers?.length === 1" class="dealership-name">{{ currentDealershipName }}</a> -->
            </div>
            <div class="right">
                <div class="document-queue">
                    <h3>Documents Waiting to be Applied to Deals: {{ documentQueue.length }}</h3>
                </div>
            </div>
        </section>
    `,
    styles: [
        `
            section.tenant {
                display: flex;
                justify-content: space-between;
                align-items: center;
                background-color: var(--tenant-header);
                padding: 1rem;
            }

            section.dealer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                background-color: var(--white);
                border-bottom: 1px solid #e2e2e2;

                .left {
                    display: flex;
                    align-items: center;
                    padding: 0.7rem;

                    img {
                        max-height: 4rem;
                        margin-right: 1rem;
                    }
                }

                .right {
                    display: flex;
                    align-items: center;

                    .document-queue {
                        padding: 0 1rem;
                    }
                }
            }

            img {
                height: 40px;
                margin-left: 1rem;
                cursor: pointer;
            }

            button {
                margin-right: 1rem;
                color: var(--white);
                cursor: pointer;
            }
        `,
    ],
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class PDDJHeader implements OnInit, OnDestroy {
    public currentDealershipName: string = null;
    private dealershipLogo: any = null;
    private isComponentAlive = true;
    public menu$: Observable<NavItem[]>;
    public selectedDealer: UserDealers = null;
    public user: User = null;
    public userDealers: UserDealers[] = [];
    public documentQueue: PddjQueue[] = [];

    constructor(
        private readonly dealershipService: DealershipService,
        private readonly documentService: DocumentsService,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly navService: NavService,
        private readonly route: ActivatedRoute,
        private readonly preloadService: PreloadService
    ) {
        // subscribe to user changes from account settings/user management
        this.userService
            .user$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                this.user = user;

                this.dealershipService
                    .getDealer$(this.user.dealerId)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe((currentDealer) => {
                        this.dealershipService.dealership = currentDealer;
                    });
            });

        // set initial dealership logo
        this.dealershipService
            .getUserDealers$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((userDealers) => {
                this.userDealers = userDealers;
                this.selectedDealer = this.userDealers?.find((d) => d.dealerId === this.user.dealerId);
            });

        this.documentService.pddjDocumentQueue$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((queue) => {
            this.documentQueue = queue;
        });
    }

    ngOnInit(): void {
        this.preloadService.preloadDealerships();
        this.getDealershipUpdates();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    private getDealershipUpdates(): void {
        this.dealershipService.dealershipUpdate$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(() => {
            this.currentDealershipName = this.dealershipService.dealershipName;
            this.setDealerLogo();
        });
    }

    public routeTo(): void {
        // if there are docs in the queue take to deals page otherwise take to status page
        this.documentService.pddjQueueVerbiage().includes('No') ? this.router.navigate(['pddj', 'status']) : this.router.navigate(['pddj', 'deals']);
    }

    public eventHandler(event: AppEvent): void {
        // update the user menu when dealer changes
        if (event.context === 'dealer-changed') {
            this.menu$ = this.navService.getUserMenu(event.data);
            this.navService.setRouteState(this.route.snapshot);
        }
    }

    private setDealerLogo(): void {
        if (this.dealershipService.dealership.file !== undefined && this.dealershipService.dealership.file !== null) {
            if (this.dealershipService.dealership.file.Body) {
                this.dealershipService.dealership.file.Body = new Uint8Array(this.dealershipService.dealership.file.Body.data);
            }
            if (this.dealershipService.dealership.file.ContentDisposition) {
                this.dealershipService.dealership.file.fileName = this.dealershipService.dealership.file.ContentDisposition.split(';')[1]
                    .split('filename')[1]
                    .split('=')[1]
                    .trim();
            }
            this.dealershipLogo = window.URL.createObjectURL(
                new Blob([this.dealershipService.dealership.file.Body], {
                    type: 'application/jpg',
                })
            );

            const image = document.getElementById('logo') as HTMLImageElement;
            if (this.dealershipService.dealership.file.content) {
                image.src = 'data:image/webp;base64, ' + this.dealershipService.dealership.file.content; // this.dealershipLogo;
            } else {
                image.src = this.dealershipLogo;
            }
        } else {
            const image = document.getElementById('logo') as HTMLImageElement;
            image.src = '../assets/img/DDJ-logo-color.png';
        }
    }
}
